import { toZonedTime } from 'date-fns-tz';
import { cloneDeep } from 'lodash-es';
import { EventPeriod, Line, PregameStatusFilters } from '@/constants';
import { isEventIdSearch } from '@/helpers/eventSearch';
import { todayDateRangeInUserTimeZone } from '@/helpers';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { PregameItem } from '@/models/pregame';
import { createPregamePageHeaders } from '@/pages/events/pregame/PregamePageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { pregameService } from '@/services/event/pregameService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { useUserStore } from '@/stores/user';
import { IPageOptions, IPregameFilters } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'eventDate', order: 'asc' as const }];

function createPregameStoreSetup(storeId: string) {
    // --- state ---
    const userStore = useUserStore();
    const items = shallowRef<PregameItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IPregameFilters>({
        sportId: createRequiredSportFilter(),
        eventDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'pregame.gameDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        eventSearch: {
            value: null,
            title: 'common.search',
            allowSaveState: false,
        },
        eventPeriodId: {
            value: EventPeriod.FullTime,
            title: 'pregame.eventPeriodId',
            allowSaveState: true,
            disableClear: true,
        },
        bookmakerId: {
            value: null,
            title: 'common.bookmakers',
            allowSaveState: true,
        },
        customLeagueLimitsCategoryId: {
            value: null,
            title: 'pregame.leagueLimitsCategory',
            allowSaveState: true,
        },
        eventStartPeriod: {
            value: null,
            title: 'pregame.eventStartPeriod',
            allowSaveState: true,
        },
        line: {
            value: Line.Main,
            title: 'pregame.line',
            allowSaveState: true,
            disableClear: true,
        },
        customLeagueId: {
            value: null,
            title: 'common.customLeague',
            allowSaveState: true,
        },
        customTeamId: {
            value: null,
            title: 'common.customTeam',
            allowSaveState: true,
        },
        invertedEvents: {
            value: null,
            title: 'pregame.inverted',
            allowSaveState: true,
        },
        eventExcludedFromFeed: {
            value: null,
            title: 'pregame.excluded',
            allowSaveState: true,
        },
        showOpenPrices: {
            value: false,
            title: 'pregame.showOpenPrices',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.eventDate.value = todayDateRangeInUserTimeZone();
        filters.eventSearch.value = null;
        filters.eventPeriodId.value = EventPeriod.FullTime;
        filters.bookmakerId.value = null;
        filters.customLeagueLimitsCategoryId.value = null;
        filters.eventStartPeriod.value = null;
        filters.line.value = Line.Main;
        filters.customLeagueId.value = null;
        filters.customTeamId.value = null;
        filters.invertedEvents.value = null;
        filters.eventExcludedFromFeed.value = null;
        filters.showOpenPrices.value = false;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createPregamePageHeaders(filters.sportId.value!));

    const availableColumns = computed(() => headers.value.filter((item) => item.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    watch(
        () => filters.sportId.value,
        () => {
            hiddenColumns.value = [];
        }
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'pregame',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            let filtersCopy = cloneDeep(filters);

            if (filtersCopy.eventStartPeriod.value) {
                const now = new Date();

                switch (filtersCopy.eventStartPeriod.value) {
                    case PregameStatusFilters.Today:
                        filtersCopy.eventDate.value = todayDateRangeInUserTimeZone();
                        break;
                    case PregameStatusFilters.Next1h:
                        filtersCopy.eventDate.value = [
                            toZonedTime(now, userStore.timeZone),
                            toZonedTime(new Date(now.getTime() + 60 * 60 * 1000), userStore.timeZone),
                        ];
                        break;
                    case PregameStatusFilters.Next2h:
                        filtersCopy.eventDate.value = [
                            toZonedTime(now, userStore.timeZone),
                            toZonedTime(new Date(now.getTime() + 2 * 60 * 60 * 1000), userStore.timeZone),
                        ];
                        break;
                    case PregameStatusFilters.Next4h:
                        filtersCopy.eventDate.value = [
                            toZonedTime(now, userStore.timeZone),
                            toZonedTime(new Date(now.getTime() + 4 * 60 * 60 * 1000), userStore.timeZone),
                        ];
                        break;
                    case PregameStatusFilters.NotStarted:
                        filtersCopy.eventDate.value = [
                            toZonedTime(now, userStore.timeZone),
                            toZonedTime(new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000), userStore.timeZone),
                        ];
                        break;
                }
                filtersCopy.eventStartPeriod.value = null;
            }

            if (isEventIdSearch(filtersCopy.eventSearch.value)) {
                filtersCopy.eventDate.value = null;
            }

            const { showOpenPrices, ...filtersForRequest } = filtersCopy;

            const params = {
                filters: filtersForRequest as IPregameFilters,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Country'],
            };

            const response = await pregameService.list(params);
            items.value = response.data.rows.map((item) => new PregameItem(item));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        stats: undefined,
    };
}

export function usePregameStore(storeId: string) {
    const useStore = defineStore(storeId, () => createPregameStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'pregame'),
    });
    return useStore();
}

export type PregameStoreSetup = ReturnType<typeof usePregameStore>;
